import { FaEye } from 'react-icons/fa';

import CustomButton from '../../../components/CustomButton';
import { Chip } from '@heroui/react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFetchUserBookings } from '../../../../hook/useQueryData';
// import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useAuth } from '../../../../context/AuthContext';
import PageHeader from '../../../components/common/ui/PageHeader';

import StatsCard from '../../../components/common/ui/StatsCard';
import { FaBookmark } from 'react-icons/fa6';
import { BsClockFill, BsFillCheckCircleFill } from 'react-icons/bs';
import { RiTimerFlashFill } from 'react-icons/ri';
import { MdCancel } from 'react-icons/md';
import moment from 'moment';
import { formatCurrency } from '../../../../utils/currency';
// import { getFavoriteCount } from '../../../../service/favoriteService';
import { useBookingsAnalytics } from '../Bookinglist/Bookinglist';
// import { Booking as BookingType } from '../../../../service/bookingService';
import type { Booking } from '../../../../utils/type';
import { formatPaymentMethod } from '../../../../utils/common';

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, userId, isAuthenticated } = useAuth();
  const {
    data: userBookingsData,
    isLoading: bookingsLoading,
    isError: bookingsError,
    error: bookingsErrorData,
  } = useFetchUserBookings(
    { userId: userId || undefined, page: 1, limit: 100 },
    { enabled: !!userId && isAuthenticated }
  );

  const { data: apiBookingsAnalytics, isFetching: isLoadingBookingAnalytics } =
    useBookingsAnalytics(userId ? userId : '', isAuthenticated);

  // Check for authentication errors
  const isAuthError =
    bookingsError &&
    ((bookingsErrorData as any)?.response?.status === 403 ||
      (bookingsErrorData as any)?.response?.status === 401);

  // const { data: favoriteCountData } = useQuery(
  //   {
  //     queryKey: ['FAVORITE-COUNT'],
  //     queryFn: getFavoriteCount,
  //     enabled: !!isAuthenticated,
  //     staleTime: 30 * 1000,
  //     refetchOnWindowFocus: false,
  //   }
  // );

  // const favoriteCount = useMemo(() => {
  //   if (!isAuthenticated) return 0;
  //   if (typeof favoriteCountData === 'number') return favoriteCountData;
  //   if (
  //     favoriteCountData &&
  //     typeof (favoriteCountData as any)?.count === 'number'
  //   ) {
  //     return (favoriteCountData as any).count;
  //   }
  //   if (
  //     favoriteCountData &&
  //     typeof (favoriteCountData as any)?.total === 'number'
  //   ) {
  //     return (favoriteCountData as any).total;
  //   }
  //   return 0;
  // }, [favoriteCountData, isAuthenticated]);

  // Always use API data; fall back to empty list and show "No data" states
  const bookingItems = useMemo<Booking[]>(() => {
    return userBookingsData?.bookings ?? [];
  }, [userBookingsData]);

  const handleViewAllBookings = useCallback(() => {
    navigate('/customer/customer-booking');
  }, [navigate]);

  // Open booking details in Booking List page (currently unused; can be re-enabled when needed)

  // Cancel booking handler is not used on dashboard; keep logic in Booking list page
  // Open booking details in Booking List page

  // Booking data (from API)

  // Get color classes for status
  type ChipColor =
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger';

  const getStatusColor = (status: any): ChipColor => {
    console.log('status:', status);
    switch (status) {
      case 'Completed':
      case 'Finished':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'In-Progress':
      case 'Confirmed':
        return 'primary';
      case 'Rescheduled':
        return 'warning';
      case 'Cancelled':
        return 'danger';
      default:
        return 'default';
    }
  };

  // StatusChip component for consistent status display
  const StatusChip = (status: any) => (
    <Chip color={getStatusColor(status)} variant="dot" size="sm" radius="sm">
      {status}
    </Chip>
  );

  // Normalize bookings to a consistent shape for the UI (image, title, etc.)
  const recentBookings = useMemo(() => {
    return (bookingItems || []).map((booking: any) => {
      // Resolve service name
      const serviceName =
        booking?.serviceName ||
        booking?.service ||
        booking?.serviceDetails?.serviceTitle ||
        'N/A';

      // Resolve images (support both gallery[] and top-level serviceImages[])
      const images: string[] = (() => {
        try {
          if (
            booking &&
            Array.isArray(booking.gallery) &&
            booking.gallery.length > 0
          ) {
            const fromGallery = booking.gallery
              .flatMap((g: { serviceImages?: string[] }) =>
                Array.isArray(g?.serviceImages) ? g.serviceImages : []
              )
              .filter(Boolean);
            if (fromGallery.length > 0) return fromGallery;
          }
          if (
            Array.isArray(booking.serviceImages) &&
            booking.serviceImages.length > 0
          ) {
            return booking.serviceImages;
          }
        } catch {
          /* noop */
        }
        return [];
      })();

      return {
        id: booking?.bookingId || booking?.id,
        serviceName,
        referenceCode: booking?.referenceCode,
        appointmentDate: booking?.appointmentDate || booking?.date,
        appointmentTimeFrom: booking?.appointmentTimeFrom,
        appointmentTimeTo: booking?.appointmentTimeTo,
        bookingStatus: booking?.bookingStatus || booking?.status,
        staffName: booking?.staffName,
        isPaid: booking?.isPaid,
        paymentMethod: booking?.paymentMethod || booking?.payment,
        total: booking?.total || booking?.amount,
        createdAt: booking?.createdAt || booking?.updatedAt,
        images,
      };
    });
  }, [bookingItems]);

  // Metrics derived from real bookings
  // const metrics = useMemo(() => {
  //   const totalOrders = bookingItems.length;
  //   const pick = (b: any) => String(b?.bookingStatus || b?.status || '');
  //   const totalCompleted = bookingItems.filter(
  //     (b) => pick(b) === 'Completed' || pick(b) === 'Finished'
  //   ).length;
  //   const totalCancelled = bookingItems.filter((b) => pick(b) === 'Cancelled').length;
  //   const totalInProgress = bookingItems.filter(
  //     (b) => pick(b) === 'InProgress' || pick(b) === 'Confirmed'
  //   ).length;
  //   const totalPending = bookingItems.filter(
  //     (b) => !['Completed', 'Finished', 'Cancelled'].includes(pick(b))
  //   ).length;
  //   return {
  //     totalOrders,
  //     totalCompleted,
  //     totalCancelled,
  //     totalPending,
  //     totalInProgress,
  //   };
  // }, [bookingItems]);

  // Chart data: status distribution
  const statusData = useMemo(() => {
    const buckets: Record<string, number> = {
      Pending: 0,
      InProgress: 0,
      Completed: 0,
      Rescheduled: 0,
      Cancelled: 0,
    };
    bookingItems.forEach((b) => {
      const raw = String(b?.bookingStatus || b?.status || 'Pending');
      if (raw === 'Completed' || raw === 'Finished') buckets.Completed++;
      else if (raw === 'Cancelled') buckets.Cancelled++;
      else if (raw === 'Rescheduled') buckets.Rescheduled++;
      else if (raw === 'InProgress' || raw === 'Confirmed')
        buckets.InProgress++;
      else buckets.Pending++;
    });
    return Object.entries(buckets)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name, value }));
  }, [bookingItems]);

  // Chart data: bookings over time (recent items)
  const timeSeriesData = useMemo(() => {
    type Row = { label: string; ts: number; count: number };
    const map = new Map<string, Row>();

    bookingItems.forEach((b) => {
      const d = b?.date || b?.appointmentDate || b?.createdAt;
      if (!d) return;
      const dt = new Date(d);
      const ts = dt.getTime();
      if (isNaN(ts)) return;
      const label = dt.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
      const row = map.get(label) || { label, ts, count: 0 };
      row.count += 1;
      map.set(label, row);
    });

    return Array.from(map.values())
      .sort((a, b) => a.ts - b.ts)
      .map(({ label, count }) => ({ date: label, count }));
  }, [bookingItems]);

  console.log('Time data: ', timeSeriesData);

  // Derive a friendly display name for the header
  const displayName = useMemo(() => {
    const first = user?.firstName;
    const last = user?.lastName;
    return first || last
      ? `${first ?? ''} ${last ?? ''}`.trim()
      : user?.name || t('customer_dashboard.welcome_fallback_name');
  }, [user, t]);

  const COLORS = ['#FFA673', '#317691', '#00C49F', '#F75270', '#898AC4'];

  // Legacy status pill styles (not used; replaced by StatusChip)

  console.log('bookingItems: ', bookingItems);

  return (
    <div>
      {/* Authentication Error Alert */}
      {isAuthError && (
        <div className="mb-4 p-4 bg-warning/10 border border-warning rounded-lg">
          <div className="flex items-start gap-3">
            <div className="text-warning text-2xl">⚠️</div>
            <div className="flex-1">
              <h4 className="text-body-bold text-warning mb-1">
                {t('customer_dashboard.session_expired')}
              </h4>
              <p className="text-body mb-3">
                {t('customer_dashboard.session_expired_msg')}
              </p>
              <CustomButton
                label={t('customer_dashboard.log_in')}
                color="warning"
                variant="solid"
                radius="sm"
                size="sm"
                onPress={() => navigate('/login')}
              />
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <PageHeader
        title={t('customer_dashboard.welcome', { name: displayName })}
        description={t('customer_dashboard.welcome_desc')}
      />

      <div className="grid grid-cols-2 tablet::grid-cols-2 laptop:grid-cols-6 flex flex-wrap gap-2 md:gap-3 mb-6 -mt-5 sm:-mt-2">
        <StatsCard
          size="md"
          isHoverEffect={true}
          title={t('customer_dashboard.total')}
          value={apiBookingsAnalytics?.totalBookings || 0}
          isLoading={isLoadingBookingAnalytics}
          icon={<FaBookmark className="text-secondary w-full h-full" />}
          iconBgClassName="bg-secondary/10"
          // valueClassName="text-secondary"
        />
        <StatsCard
          size="md"
          isHoverEffect={true}
          title={t('customer_dashboard.pending')}
          value={apiBookingsAnalytics?.pendingCount || 0}
          isLoading={isLoadingBookingAnalytics}
          icon={<BsClockFill className="text-warning w-full h-full" />}
          iconBgClassName="bg-warning/10"
          // valueClassName="text-warning"
        />
        <StatsCard
          size="md"
          isHoverEffect={true}
          title={t('customer_dashboard.confirmed')}
          value={apiBookingsAnalytics?.confirmedCount || 0}
          isLoading={isLoadingBookingAnalytics}
          icon={
            <BsFillCheckCircleFill className="text-primary w-full h-full" />
          }
          iconBgClassName="bg-primary/10"
          // valueClassName="text-primary"
        />
        <StatsCard
          size="md"
          isHoverEffect={true}
          title={t('customer_dashboard.in_progress')}
          value={apiBookingsAnalytics?.inprogressCount || 0}
          isLoading={isLoadingBookingAnalytics}
          icon={<RiTimerFlashFill className="text-primary w-full h-full" />}
          iconBgClassName="bg-primary/10"
          // valueClassName="text-primary"
        />
        <StatsCard
          size="md"
          isHoverEffect={true}
          title={t('customer_dashboard.completed')}
          value={apiBookingsAnalytics?.completedCount || 0}
          isLoading={isLoadingBookingAnalytics}
          icon={
            <BsFillCheckCircleFill className="text-success w-full h-full" />
          }
          iconBgClassName="bg-success/10"
          // valueClassName="text-success"
        />
        <StatsCard
          size="md"
          isHoverEffect={true}
          title={t('customer_dashboard.cancelled')}
          value={apiBookingsAnalytics?.cancelledCount || 0}
          isLoading={isLoadingBookingAnalytics}
          icon={<MdCancel className="text-danger w-full h-full" />}
          iconBgClassName="bg-danger/10"
          // valueClassName="text-danger"
        />
      </div>

      {/* Insights */}
      <div className="grid grid-cols-12 gap-4 md:gap-3 mb-5">
        {/* Bookings Over Time */}
        <div className="col-span-12 lg:col-span-8 bg-white p-3 md:px-4  border border-secondary/40  hover:shadow-sm transition-shadow duration-300 rounded-xl">
          <div className="flex justify-between items-center p-2 mb-4">
            <div className="flex flex-col">
              <h3 className="text-title-bold">
                {t('customer_dashboard.bookings_over_time')}
              </h3>
              <p className="text-caption">
                {t('customer_dashboard.bookings_over_time_desc')}
              </p>
            </div>
          </div>

          <div className="h-64 -ml-10">
            {bookingsLoading ? (
              <div className="h-full ">
                <div className="h-full w-full rounded-lg bg-gray-100" />
              </div>
            ) : bookingsError ? (
              <div className="h-full flex items-center justify-center text-danger">
                {t('customer_dashboard.failed_load_bookings')}
              </div>
            ) : timeSeriesData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeSeriesData}>
                  <defs>
                    <linearGradient
                      id="colorBookings"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#0096FF"
                        stopOpacity={0.35}
                      />
                      <stop
                        offset="95%"
                        stopColor="#0047AB"
                        stopOpacity={0.06}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#0096FF"
                    strokeWidth={1}
                    fillOpacity={1}
                    fill="url(#colorBookings)"
                    isAnimationActive={true}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                {t('customer_dashboard.no_time_series')}
              </div>
            )}
          </div>
        </div>

        {/* Status Breakdown */}
        <div className="col-span-12 lg:col-span-4 bg-white p-5 md:p-5  border border-secondary/40 transition-shadow duration-300 rounded-xl">
          <div className="flex justify-between items-center  pb-2 mb-4">
            <div className="flex flex-col">
              <h3 className="text-title-bold">
                {t('customer_dashboard.status_breakdown')}
              </h3>
              <p className="text-caption">
                {t('customer_dashboard.status_breakdown_desc')}
              </p>
            </div>
          </div>
          <div className="h-64">
            {bookingsLoading ? (
              <div className="h-full ">
                <div className="h-full w-full rounded-lg bg-gray-100" />
              </div>
            ) : bookingsError ? (
              <div className="h-full flex items-center justify-center text-danger">
                {t('customer_dashboard.failed_load_status')}
              </div>
            ) : statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={4}
                    isAnimationActive={true}
                    label={({ name }) => name}
                    style={{
                      fontSize: '12px',
                    }}
                  >
                    {statusData.map((entry, index) => (
                      <Cell
                        key={`cell-${entry.name}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>

                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                {t('customer_dashboard.no_status_data')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Bookings */}
      <div className="bg-white p-3  border border-secondary/40 transition-shadow duration-300 rounded-xl">
        <div className="flex justify-between items-center mb-3">
          <div className="flex flex-col">
            <h3 className="text-title-bold">
              {t('customer_dashboard.recent_bookings')}
            </h3>
            <p className="text-caption">
              {t('customer_dashboard.recent_bookings_desc')}
            </p>
          </div>
          <CustomButton
            label={t('customer_dashboard.view_all')}
            color="primary"
            variant="solid"
            size="sm"
            radius="sm"
            endContent={<FaEye />}
            onPress={handleViewAllBookings}
          />
        </div>

        <div className="border border-gray-200 rounded-md  overflow-hidden">
          {/* This div makes the table scrollable on small screens */}
          <div className="overflow-x-auto">
            <table className="w-full text-body text-left text-gray-600">
              <thead className="bg-secondary/20 text-body-bold">
                <tr>
                  <th scope="col" className="px-3 py-3 text-body-bold">
                    {t('customer_dashboard.col_name')}
                  </th>
                  <th scope="col" className="px-6 py-3">
                    {t('customer_dashboard.col_booking_date')}
                  </th>
                  <th scope="col" className="px-6 py-3">
                    {t('customer_dashboard.col_appointment_time')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left">
                    {t('customer_dashboard.col_status')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left">
                    {t('customer_dashboard.col_staff_name')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left">
                    {t('customer_dashboard.col_paid_status')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left">
                    {t('customer_dashboard.col_payment_method')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left">
                    {t('customer_dashboard.col_total')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left">
                    {t('customer_dashboard.col_created_on')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {bookingsLoading && recentBookings.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-10 text-center text-gray-500">
                      Loading recent bookings...
                    </td>
                  </tr>
                ) : bookingsError ? (
                  <tr>
                    <td colSpan={9} className="py-10 text-center text-danger">
                      Failed to load recent bookings.
                    </td>
                  </tr>
                ) : recentBookings?.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-10 text-center text-gray-500">
                      No Data found
                    </td>
                  </tr>
                ) : (
                  <>
                    {recentBookings?.slice(0, 6).map((b: any) => (
                      <tr
                        key={b.id}
                        className="bg-white border-b hover:bg-secondary/10 text-left"
                      >
                        <td className="px-3 py-2 font-medium whitespace-pre-wrap items-center">
                          <div className="flex items-center">
                            <img
                              className="h-8 w-8 rounded-full object-cover mr-3"
                              src={b?.images?.[0] || '/fallback-profile-1.avif'}
                              alt={`${b.serviceName} cover`}
                            />
                            <div>
                              <div className="font-semibold">
                                {b.serviceName}
                              </div>
                              <div className="text-gray-500 text-xs">
                                {b.referenceCode || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {b?.appointmentDate
                            ? moment(b?.appointmentDate).format('DD MMM YYYY')
                            : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          {b?.appointmentTimeFrom && b?.appointmentTimeTo
                            ? `${b?.appointmentTimeFrom} - ${b?.appointmentTimeTo}`
                            : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          {/* <span
                        className={`px-2 py-1 font-semibold leading-tight rounded-full text-xs ${getStatusStyles(b.bookingStatus)}`}
                      >
                        {b?.bookingStatus || 'N/A'}
                      </span> */}
                          {StatusChip(
                            b?.bookingStatus === 'InProgress'
                              ? 'In-Progress'
                              : b?.bookingStatus || 'N/A'
                          )}
                        </td>
                        <td className="px-6 py-4">{b?.staffName || 'N/A'}</td>
                        <td className="px-6 py-4">
                          {b?.isPaid ? 'Yes' : 'No'}
                        </td>
                        <td className="px-6 py-4">
                          {formatPaymentMethod(b?.paymentMethod) || 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          {formatCurrency(Number(b?.total)) || 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          {b?.createdAt
                            ? moment(b?.createdAt).format('DD MMM YYYY')
                            : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
